import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Utensils, Plus, Trash2, Check, Sparkles, Upload, Camera } from "lucide-react";
import { toast } from "sonner";
import API, { getAuthHeaders, hasToken, BACKEND_URL } from "@/lib/api";

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function DishVoting({ eventId, isOwner }) {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDish, setNewDish] = useState("");
  const [hasVoted, setHasVoted] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [allowGuestSuggestions, setAllowGuestSuggestions] = useState(false);

  useEffect(() => {
    fetchDishes();
    checkIfVoted();
    fetchEventSettings();
  }, [eventId]);

  const fetchEventSettings = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}`, authConfig());
      setAllowGuestSuggestions(res.data.allow_guest_dish_suggestions || false);
    } catch (error) {
      console.error("Failed to fetch event settings:", error);
    }
  };

  const handleToggleGuestSuggestions = async (checked) => {
    setAllowGuestSuggestions(checked);
    try {
      await axios.put(`${API}/events/${eventId}`, { allow_guest_dish_suggestions: checked }, authConfig());
      toast.success("Einstellung gespeichert!");
    } catch (error) {
      console.error("Failed to update setting:", error);
      toast.error("Speichern fehlgeschlagen");
      setAllowGuestSuggestions(!checked);
    }
  };

  const fetchDishes = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/dish-voting`, authConfig());
      setDishes(response.data);
    } catch (error) {
      console.error("Failed to fetch dishes:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkIfVoted = () => {
    const voted = localStorage.getItem(`dish_voted_${eventId}`);
    setHasVoted(!!voted);
  };

  /* New State for Image Upload */
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleAddDish = async (e) => {
    e.preventDefault();
    if (!newDish.trim()) return;

    setIsAdding(true);
    let imageUrl = null;

    try {
      // 1. Upload custom image if selected
      if (selectedImage) {
        const formData = new FormData();
        formData.append('photo', selectedImage);

        // Use existing photo upload endpoint which returns persistent URLs
        const uploadRes = await axios.post(
          `${API}/events/${eventId}/photos`,
          formData,
          {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // The endpoint returns a single photo object (or array)
        if (uploadRes.data) {
          if (Array.isArray(uploadRes.data) && uploadRes.data.length > 0) {
            imageUrl = uploadRes.data[0].url;
          } else if (uploadRes.data.url) {
            imageUrl = uploadRes.data.url;
          }
        }
      }

      // 2. Add dish with (optional) image URL
      await axios.post(
        `${API}/events/${eventId}/dish-voting`,
        { dish_name: newDish, image_url: imageUrl },
        authConfig()
      );

      setNewDish("");
      setSelectedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      toast.success("Gericht hinzugefügt!");
      fetchDishes();
    } catch (error) {
      console.error("Failed to add dish:", error);
      toast.error("Fehler beim Hinzufügen");
    } finally {
      setIsAdding(false);
    }
  };

  const handleVote = async (dishId) => {
    try {
      await axios.post(`${API}/events/${eventId}/dish-voting/${dishId}/vote`, {}, authConfig());
      toast.success("Stimme abgegeben!");
      localStorage.setItem(`dish_voted_${eventId}`, "true");
      setHasVoted(true);
      fetchDishes();
    } catch (error) {
      console.error("Failed to vote:", error);
      toast.error("Fehler beim Abstimmen");
    }
  };

  const handleDelete = async (dishId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/dish-voting/${dishId}`,
        authConfig()
      );
      toast.success("Gericht entfernt");
      fetchDishes();
    } catch (error) {
      console.error("Failed to delete dish:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const totalVotes = dishes.reduce((sum, d) => sum + (d.votes || 0), 0);
  const sortedDishes = [...dishes].sort((a, b) => (b.votes || 0) - (a.votes || 0));

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-xl font-black flex items-center gap-2 mb-2 uppercase tracking-tight">
          <Utensils className="w-6 h-6 text-brand-accent" />
          Menü-Planung & Voting
        </h3>
        <p className="text-sm text-muted-foreground font-medium">
          {isOwner
            ? "Mache Menü-Vorschläge. Die KI generiert automatisch passende Bilder für ein schöneres Voting."
            : hasVoted
              ? "Danke für deine Stimme! Deine Auswahl hilft uns bei der Planung."
              : "Welches der folgenden Gerichte würdest du am liebsten essen?"
          }
        </p>
      </div>

      {/* Add Dish Settings Toggle (Owner only) */}
      {isOwner && (
        <div className="mb-4 flex items-center justify-between p-4 border rounded-2xl bg-slate-50 border-slate-200">
          <div>
            <p className="text-sm font-bold text-slate-900">Gäste dürfen Gerichte vorschlagen</p>
            <p className="text-xs text-muted-foreground mt-1">Erlaube deinen Besuchern, eigene Menü-Ideen einzubringen.</p>
          </div>
          <Switch
            checked={allowGuestSuggestions}
            onCheckedChange={handleToggleGuestSuggestions}
            className="data-[state=checked]:bg-emerald-500"
          />
        </div>
      )}

      {/* Add Dish Form (Owner or Guests if allowed) */}
      {(isOwner || allowGuestSuggestions) && (
        <form onSubmit={handleAddDish} className="mb-8 p-6 border rounded-3xl space-y-4 bg-brand-accent/5 border-brand-accent/10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-accent" />
            <Label className="font-black uppercase tracking-widest text-[10px] text-brand-accent">Neuen Menü-Vorschlag erstellen</Label>
          </div>
          <div className="flex flex-col gap-3">
            <Input
              value={newDish}
              onChange={(e) => setNewDish(e.target.value)}
              placeholder="z.B. Zarter Rehrücken an Waldpilzen"
              className="rounded-xl bg-white border-gray-200"
              disabled={isAdding}
            />

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleImageSelect}
            />

            {!selectedImage ? (
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={(e) => handleAddDish(e)}
                  disabled={!newDish.trim() || isAdding}
                  className="flex-1 bg-purple-100 hover:bg-purple-200 text-purple-700 border border-purple-200 shadow-sm rounded-xl font-bold text-xs h-10"
                >
                  <Sparkles className="w-3 h-3 mr-2" />
                  Mit KI erstellen
                </Button>
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAdding}
                  variant="outline"
                  className="flex-1 bg-white hover:bg-gray-50 text-gray-700 border-dashed border-gray-300 rounded-xl font-bold text-xs h-10"
                >
                  <Camera className="w-3 h-3 mr-2" />
                  Eigenes Foto
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                <div className="w-8 h-8 rounded bg-emerald-200 flex items-center justify-center text-emerald-700 flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-emerald-800 font-bold truncate">{selectedImage.name}</p>
                  <button type="button" onClick={() => setSelectedImage(null)} className="text-[10px] text-red-500 hover:underline">Entfernen</button>
                </div>
                <Button
                  type="button"
                  onClick={(e) => handleAddDish(e)}
                  disabled={!newDish.trim() || isAdding}
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3"
                >
                  {isAdding ? <Plus className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                  Speichern
                </Button>
              </div>
            )}
          </div>
        </form>
      )}

      {/* Total Votes */}
      {totalVotes > 0 && (
        <div className="mb-4 p-3 bg-secondary/20 rounded-lg text-center">
          <p className="text-sm text-muted-foreground">Gesamt</p>
          <p className="text-2xl font-bold">{totalVotes} {totalVotes === 1 ? 'Stimme' : 'Stimmen'}</p>
        </div>
      )}

      {/* Dishes List */}
      <div className="space-y-3">
        {dishes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Utensils className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Gerichte</p>
            {isOwner && (
              <p className="text-xs mt-1">Füge das erste Gericht hinzu!</p>
            )}
          </div>
        ) : (
          sortedDishes.map((dish, idx) => {
            const percentage = totalVotes > 0 ? Math.round((dish.votes / totalVotes) * 100) : 0;

            return (
              <div key={dish.id} className="relative">
                <div className="p-0 rounded-2xl border bg-card overflow-hidden shadow-sm hover:shadow-md transition-all">
                  <div className="flex flex-col sm:flex-row">
                    {/* Dish Image */}
                    <div className="w-full sm:w-32 h-32 sm:h-auto overflow-hidden bg-gray-100 flex-shrink-0 relative group-hover:scale-105 transition-transform duration-700">
                      {dish.image_url ? (
                        <img
                          src={dish.image_url.startsWith('http') ? dish.image_url : (dish.image_url.startsWith('/api') ? `${BACKEND_URL}${dish.image_url}` : `${API}${dish.image_url}`)}
                          className="w-full h-full object-cover"
                          alt={dish.dish_name}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}

                      {/* Placeholder / Fallback */}
                      <div
                        className="absolute inset-0 flex items-center justify-center text-gray-300 bg-gray-100"
                        style={{ display: dish.image_url ? 'none' : 'flex' }}
                      >
                        <Utensils className="w-8 h-8 opacity-20" />
                      </div>
                    </div>

                    <div className="flex-1 p-4 relative">
                      {/* Progress Bar Background */}
                      <div
                        className="absolute inset-0 bg-primary/5 transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>

                      <div className="relative flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                          {idx === 0 && dish.votes > 0 && (
                            <span className="text-2xl">🏆</span>
                          )}
                          <div className="flex-1">
                            <p className="font-bold text-lg text-gray-900">{dish.dish_name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                              <span className="font-black text-primary">{percentage}%</span>
                              <span>•</span>
                              <span>{dish.votes || 0} {(dish.votes || 0) === 1 ? 'Stimme' : 'Stimmen'}</span>
                            </p>
                          </div>
                        </div>



                        <div className="flex items-center gap-2">
                          {!hasVoted && (
                            <Button
                              size="sm"
                              onClick={() => handleVote(dish.id)}
                              className="rounded-full font-bold uppercase tracking-wider text-[10px]"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Wählen
                            </Button>
                          )}

                          {isOwner && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDelete(dish.id)}
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {
        hasVoted && !isOwner && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
            <p className="text-sm text-green-700 font-medium">✓ Du hast bereits abgestimmt</p>
          </div>
        )
      }
    </Card >
  );
}